import React, { ReactNode, useEffect } from "react";
import FormProgressBar from "@/components/subcomponents/FormProgressBar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetApplicationQuery, useGetApplicationStepStatusQuery } from "@/store/service";
import Skeleton from "react-loading-skeleton";
import { getUrlStepFromApplicationStep } from "@/util/url-utils";
import { ApplicationStep } from "@/enum/ApplicationStep";
import { ApplicationStepStatus } from "@/enum/ApplicationStepStatus";
import {
  User,
  Home,
  Users,
  MapPin,
  Fingerprint,
  Building2,
  Briefcase,
  Wallet,
  CreditCard,
  Landmark,
  FileCheck,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

export interface ApplicationFormDetails {
  name: string;
  path: string;
  step: ApplicationStep;
  icon?: LucideIcon;
  children?: ApplicationFormChildDetails[];
}

export interface ApplicationFormChildDetails extends ApplicationFormDetails {
  applyCoapplicant?: boolean;
}

const getCommonApplicantSteps = (type: "main-applicant" | "co-applicant") => {
  const commonApplicantSteps: ApplicationFormChildDetails[] = [
    {
      name: "Applicant",
      path: "/applicant",
      icon: User,
      step: type === "main-applicant" ? ApplicationStep.Applicant : ApplicationStep.CoApplicant,
    },
    {
      name: "Address",
      path: `/applicant-address`,
      icon: MapPin,
      step: type === "main-applicant" ? ApplicationStep.ApplicantAddress : ApplicationStep.CoApplicantAddress,
      applyCoapplicant: true,
    },
    {
      name: "100 Points ID",
      path: `/applicant-id`,
      icon: Fingerprint,
      step: type === "main-applicant" ? ApplicationStep.ApplicantID : ApplicationStep.CoApplicantID,
      applyCoapplicant: true,
    },
    {
      name: "Mortgage",
      path: `/applicant-current-mortgage`,
      icon: Building2,
      step:
        type === "main-applicant"
          ? ApplicationStep.ApplicantCurrentMortgage
          : ApplicationStep.CoApplicantCurrentMortgage,
      applyCoapplicant: true,
    },
    {
      name: "Employment",
      path: `/applicant-employment`,
      icon: Briefcase,
      step: type === "main-applicant" ? ApplicationStep.ApplicantEmployment : ApplicationStep.CoApplicantEmployment,
      applyCoapplicant: true,
    },
    {
      name: "Assets",
      path: `/assets`,
      icon: Wallet,
      step: type === "main-applicant" ? ApplicationStep.ApplicantAssets : ApplicationStep.CoApplicantAssets,
      applyCoapplicant: true,
    },
    {
      name: "Liabilities",
      path: `/liabilities`,
      icon: CreditCard,
      step: type === "main-applicant" ? ApplicationStep.ApplicantLiabilities : ApplicationStep.CoApplicantLiabilities,
      applyCoapplicant: true,
    },
  ];

  let applicantSteps = commonApplicantSteps.map((step) => ({
    ...step,
    path: `${step.path}/${type}`,
  }));

  if (type === "co-applicant") {
    return applicantSteps.filter((s) => s.applyCoapplicant);
  }

  return applicantSteps;
};

const getApplicationSteps = (hasCoapplicant: boolean) => {
  const applicationSteps: ApplicationFormDetails[] = [
    {
      name: "Fintree account",
      path: "/user/",
      icon: User,
      step: ApplicationStep.User,
    },
    {
      name: "Property details",
      path: "/context/",
      icon: Home,
      step: ApplicationStep.Context,
    },
    {
      name: "Applicant Details",
      path: "/applicant/main-applicant",
      icon: Users,
      step: ApplicationStep.Applicant,
      children: getCommonApplicantSteps("main-applicant"),
    },
    {
      name: "Banking Details",
      path: "/banking-details",
      icon: Landmark,
      step: ApplicationStep.ApplicantBankingDetails,
    },
    {
      name: "Self Declaration",
      path: "/self-declaration",
      icon: FileCheck,
      step: ApplicationStep.SelfDeclaration,
    },
  ];

  if (hasCoapplicant) {
    applicationSteps.splice(3, 0, {
      name: "Coapplicant Details",
      path: "/applicant/co-applicant",
      icon: UserPlus,
      step: ApplicationStep.CoApplicant,
      children: getCommonApplicantSteps("co-applicant"),
    });
  }

  return applicationSteps;
};

const SteppedApplicationContainer = ({ children }: { children: ReactNode }) => {
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const applicationId = params.id ?? "";

  const {
    data: application,
    isFetching,
    isError,
  } = useGetApplicationQuery(applicationId, {
    skip: !applicationId,
  });
  const { data: stepData, isFetching: isFetchingSteps } = useGetApplicationStepStatusQuery(applicationId, {
    skip: !applicationId,
  });
  const stepStatuses = stepData as Record<ApplicationStep, ApplicationStepStatus>;

  const hasCoApplicant = !!application?.applicants?.find((applicant) => applicant.type === "CoApplicant");

  useEffect(() => {
    if (!applicationId) {
      navigate("/");
      return;
    }
  }, [applicationId]);

  useEffect(() => {
    if (!isFetching && !application && isError) {
      navigate("/");
    }

    if (params.type && params.type.includes("cooapplicant") && !hasCoApplicant) {
      const currentStep = application?.step;
      if (currentStep) {
        navigate(`/application/${params.id}/${getUrlStepFromApplicationStep(application?.step)}`);
      }

      navigate("/");
    }
  }, [isFetching, application, isError]);

  if (!applicationId || (!application && isFetching)) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-64 flex-shrink-0 hidden md:block">
            <Skeleton className="mt-2" count={5} height="3em" />
          </div>
          <div className="flex-1">
            <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="max-w-2xl mx-auto">
                <Skeleton className="mb-4" height="2em" width="60%" />
                {Array.from({ length: 4 }).map((v, i) => (
                  <div key={`scs-${i}`} className="mt-4">
                    <Skeleton height="1.25em" width="6em" />
                    <Skeleton height="2.75em" className="mt-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const steps = getApplicationSteps(hasCoApplicant);
  const currentStepIndex = steps.findIndex((s) => {
    return pathname.includes(s.path);
  });

  return (
    <div className="w-full px-4 py-8">
      <div className="flex flex-col md:flex-row gap-8 max-w-6xl mx-auto">
        <div className="w-full md:w-64 flex-shrink-0 flex-grow-0">
          <div className="md:sticky md:top-8">
            {!stepStatuses && isFetchingSteps ? (
              <div className="hidden md:block">
                <Skeleton className="mt-2" count={5} height="3em" />
              </div>
            ) : (
              <FormProgressBar activeStepIndex={currentStepIndex} steps={steps} stepStatuses={stepStatuses} />
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0 min-h-[60vh]">
          <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 max-w-2xl">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default SteppedApplicationContainer;
